import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Printer,
  MapPin,
  Calendar,
  Receipt,
  CreditCard,
  TrendingUp,
  DollarSign,
  Users,
  ShoppingBag,
  Scissors,
  Store,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { SubPageHeader } from "../../components/ui/SubPageHeader";

interface TenderItem {
  method: string;
  in: number;
  out: number;
  total: number;
}

interface ZReportDetailData {
  id: string;
  businessInfo: {
    name: string;
    address: string;
    closeOutNumber: number;
    beginTime: string;
    endTime: string;
  };
  metrics: {
    transactions: number;
    totalTax: number;
    totalNetSale: number;
    totalSales: number;
    totalQuickPayments: number;
  };
  details: {
    totalDiscount: number;
    totalRefunds: number;
    refundsNet: number;
    refundsTaxGross: number;
  };
  checkUp: {
    tender: TenderItem[];
    total: number;
  };
  salesByService: {
    items: {
      name: string;
      amount: number;
    }[];
    totalNet: number;
    total: number;
  };
  salesByProduct: {
    totalNet: number;
    total: number;
  };
  salesByEmployee: {
    items: {
      name: string;
      amount: number;
    }[];
    total: number;
  };
}

const getMockReportDetail = (id: string): ZReportDetailData => {
  return {
    id,
    businessInfo: {
      name: "My Salon Business",
      address: "123 Business Road, City, Country",
      closeOutNumber: 1,
      beginTime: "16/12/2025 12:00",
      endTime: "16/12/2025 07:09",
    },
    metrics: {
      transactions: 7,
      totalTax: 0.0,
      totalNetSale: 431.0,
      totalSales: 431.0,
      totalQuickPayments: 0.0,
    },
    details: {
      totalDiscount: 0.0,
      totalRefunds: 0.0,
      refundsNet: 0.0,
      refundsTaxGross: 0.0,
    },
    checkUp: {
      tender: [
        { method: "Cash", in: 0.0, out: 0.0, total: 0.0 },
        { method: "Card", in: 0.0, out: 0.0, total: 0.0 },
        { method: "Cheque", in: 0.0, out: 0.0, total: 0.0 },
      ],
      total: 0.0,
    },
    salesByService: {
      items: [
        {
          name: "Adults Haircut with or without Beard (1)",
          amount: 25.0,
        },
        {
          name: "adult under-cut with beard (1)",
          amount: 20.0,
        },
      ],
      totalNet: 439.1,
      total: 45.0,
    },
    salesByProduct: {
      totalNet: 0.0,
      total: 0.0,
    },
    salesByEmployee: {
      items: [
        { name: "Malik Griffith", amount: 431.0 },
        { name: "Muhammad Ishaq", amount: 0.0 },
      ],
      total: 431.0,
    },
  };
};

const StatCard = ({
  label,
  value,
  icon: Icon,
  subValue,
}: {
  label: string;
  value: string | number;
  icon: any;
  subValue?: string;
  highlight?: boolean;
}) => (
  <div className="p-5 rounded-2xl border border-gray-100 bg-white text-gray-900 shadow-sm">
    <div className="flex justify-between items-start mb-4">
      <div className="p-2 rounded-xl bg-purple-50">
        <Icon size={20} className="text-[#885ABB]" />
      </div>
      {subValue && (
        <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-50 text-green-600">
          {subValue}
        </span>
      )}
    </div>
    <div className="space-y-1">
      <h3 className="text-sm font-medium text-gray-500">{label}</h3>
      <p className="text-2xl font-bold tracking-tight">
        {typeof value === "number" ? `£${value.toFixed(2)}` : value}
      </p>
    </div>
  </div>
);

const SectionHeader = ({
  title,
  icon: Icon,
}: {
  title: string;
  icon?: any;
}) => (
  <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
    {Icon && <Icon size={20} className="text-[#885ABB]" />}
    <h2 className="text-lg font-bold text-gray-900">{title}</h2>
  </div>
);

const DetailRow = ({
  label,
  value,
  isTotal = false,
}: {
  label: string;
  value: string | number;
  isTotal?: boolean;
}) => (
  <div
    className={`flex justify-between items-center py-3 ${
      isTotal ? "border-t border-gray-100 mt-2 pt-4" : ""
    }`}
  >
    <span
      className={`${
        isTotal
          ? "text-base font-bold text-gray-900"
          : "text-sm font-medium text-gray-500"
      }`}
    >
      {label}
    </span>
    <span
      className={`${
        isTotal
          ? "text-lg font-bold text-[#885ABB]"
          : "text-sm font-bold text-gray-700"
      }`}
    >
      {typeof value === "number" ? `£${value.toFixed(2)}` : value}
    </span>
  </div>
);

const ZReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ZReportDetailData | null>(null);

  useEffect(() => {
    if (id) setData(getMockReportDetail(id));
  }, [id]);

  if (!data) return <div>Loading...</div>;

  return (
    <div className="flex-1 flex flex-col space-y-8 p-6 bg-gray-50/50 min-h-screen">
      {/* Navigation Header */}
      <SubPageHeader
        title={`Z-Report #${data.businessInfo.closeOutNumber}`}
        subtitle="Comprehensive breakdown of daily closing report"
        icon={Receipt}
        actions={
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="bg-white hover:bg-gray-50 text-gray-700 border-gray-200"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={16} className="mr-2" />
              Back
            </Button>
            <Button className="bg-[#885ABB] hover:bg-[#7748A4] text-white shadow-lg shadow-purple-200">
              <Printer size={16} className="mr-2" />
              Print Report
            </Button>
          </div>
        }
      />

      {/* Top Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          label="Total Sales"
          value={data.metrics.totalSales}
          icon={TrendingUp}
          highlight
          subValue="+12.5% vs avg"
        />
        <StatCard
          label="Total Net Sale"
          value={data.metrics.totalNetSale}
          icon={DollarSign}
        />
        <StatCard
          label="Transactions"
          value={data.metrics.transactions}
          icon={Receipt}
        />
        <StatCard label="Sales by Card" value={407.0} icon={CreditCard} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Main Detailed Tables */}
        <div className="lg:col-span-2 space-y-8">
          {/* Cash Up Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <SectionHeader title="Check Up & Tender" icon={CreditCard} />

            <div className="overflow-hidden rounded-xl border border-gray-100">
              <div className="grid grid-cols-4 bg-gray-50 p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <div>Type</div>
                <div className="text-right">In</div>
                <div className="text-right">Out</div>
                <div className="text-right">Total</div>
              </div>
              <div className="divide-y divide-gray-100">
                {data.checkUp.tender.map((item, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-4 p-4 hover:bg-purple-50/10 transition-colors"
                  >
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          item.total > 0 ? "bg-green-500" : "bg-gray-300"
                        }`}
                      />
                      {item.method}
                    </div>
                    <div className="text-right text-gray-600 font-mono">
                      £{item.in.toFixed(2)}
                    </div>
                    <div className="text-right text-gray-600 font-mono">
                      £{item.out.toFixed(2)}
                    </div>
                    <div className="text-right font-bold text-gray-900 font-mono">
                      £{item.total.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 p-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-gray-900">
                    Total Tendered
                  </span>
                  <span className="text-xl font-bold text-[#885ABB]">
                    £{data.checkUp.total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sales by Service */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
              <SectionHeader title="Sales by Service" icon={Scissors} />
              <div className="space-y-4 flex-1">
                {data.salesByService.items.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-start group"
                  >
                    <span className="text-sm text-gray-600 group-hover:text-[#885ABB] transition-colors">
                      {item.name}
                    </span>
                    <span className="text-sm font-bold text-gray-900">
                      £{item.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <DetailRow
                  label="Total Net"
                  value={data.salesByService.totalNet}
                  isTotal
                />
              </div>
            </div>

            {/* Sales by Employee */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col">
              <SectionHeader title="Sales by Employee" icon={Users} />
              <div className="space-y-4 flex-1">
                {data.salesByEmployee.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-[#885ABB]">
                        {item.name.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {item.name}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">
                      £{item.amount.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <DetailRow
                  label="Total Sales"
                  value={data.salesByEmployee.total}
                  isTotal
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Info & Summary */}
        <div className="lg:col-span-1 space-y-6">
          {/* Business Profile Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-0 overflow-hidden">
            <div className="bg-white p-6 border-b-2 border-b-[#885ABB]">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Store className="text-[#885ABB]" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-900">{data.businessInfo.name}</h2>
              </div>
              <p className="text-gray-500 text-sm flex items-center gap-2 pl-12">
                <MapPin size={14} className="text-[#885ABB]" />
                {data.businessInfo.address}
              </p>
            </div>

            <div className="px-6 pt-6">
              <div className="bg-purple-50 rounded-xl border border-purple-100 p-4 space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-dashed border-gray-100">
                  <span className="text-xs font-semibold text-gray-400 uppercase">
                    Start Time
                  </span>
                  <span className="text-sm font-bold text-gray-800">
                    {data.businessInfo.beginTime}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-gray-400 uppercase">
                    End Time
                  </span>
                  <span className="text-sm font-bold text-gray-800">
                    {data.businessInfo.endTime}
                  </span>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6 pt-0 space-y-1">
              <DetailRow label="Total Tax" value={data.metrics.totalTax} />
              <DetailRow
                label="Quick Payments"
                value={data.metrics.totalQuickPayments}
              />
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-6">
              Financial Summary
            </h3>
            <div className="space-y-1">
              <DetailRow
                label="Total Discount"
                value={data.details.totalDiscount}
              />
              <DetailRow
                label="Total Refunds"
                value={data.details.totalRefunds}
              />
              <DetailRow label="Refunds Net" value={data.details.refundsNet} />
              <DetailRow
                label="Refunds Tax Gross"
                value={data.details.refundsTaxGross}
              />
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex justify-between items-end mb-2">
                <span className="text-sm font-medium text-gray-500">
                  Net Sales
                </span>
                <span className="text-lg font-bold text-gray-900">
                  £{data.metrics.totalNetSale.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-end mb-4">
                <span className="text-sm font-medium text-gray-500">Tax</span>
                <span className="text-lg font-bold text-gray-900">
                  £{data.metrics.totalTax.toFixed(2)}
                </span>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl flex justify-between items-center">
                <span className="font-bold text-[#885ABB]">Total Revenue</span>
                <span className="text-2xl font-bold text-[#885ABB]">
                  £{data.metrics.totalSales.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZReportDetail;
